import { useEffect, useState } from 'react';
import { api } from './api';

export type MediaVariant = 'proxy' | 'proxySd' | 'poster' | 'filmstrip' | 'waveform' | 'original';
type Urls = Partial<Record<MediaVariant, string>>;

/**
 * Batches requests for signed media URLs and caches them until shortly
 * before they expire. Keys are asset ids.
 */
const cache = new Map<string, { urls: Urls; expiresAt: number; version: string }>();
const waiting = new Map<string, ((u: Urls) => void)[]>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  timer = null;
  const ids = [...waiting.keys()];
  if (ids.length === 0) return;
  const callbacks = new Map(waiting);
  waiting.clear();
  api<{ urls: Record<string, Urls>; expiresAt: number }>('/api/media/urls', {
    method: 'POST',
    json: { assetIds: ids, variants: ['proxy', 'proxySd', 'poster', 'filmstrip', 'waveform', 'original'] },
  })
    .then((res) => {
      for (const id of ids) {
        const urls = res.urls[id] ?? {};
        cache.set(id, { urls, expiresAt: res.expiresAt - 10 * 60_000, version: versions.get(id) ?? '' });
        for (const cb of callbacks.get(id) ?? []) cb(urls);
      }
    })
    .catch(() => {
      for (const id of ids) for (const cb of callbacks.get(id) ?? []) cb({});
    });
}

/** Asset "version" so URLs refresh when processing adds new derivatives. */
const versions = new Map<string, string>();

export function mediaUrls(assetId: string, version = ''): Promise<Urls> {
  versions.set(assetId, version);
  const hit = cache.get(assetId);
  if (hit && hit.expiresAt > Date.now() && hit.version === version) return Promise.resolve(hit.urls);
  return new Promise((resolve) => {
    const list = waiting.get(assetId) ?? [];
    list.push(resolve);
    waiting.set(assetId, list);
    if (!timer) timer = setTimeout(flush, 15);
  });
}

export function cachedMediaUrls(assetId: string): Urls | null {
  const hit = cache.get(assetId);
  return hit && hit.expiresAt > Date.now() ? hit.urls : null;
}

export function useMediaUrls(assetId: string | null | undefined, version = ''): Urls {
  const [urls, setUrls] = useState<Urls>(() => (assetId ? cachedMediaUrls(assetId) ?? {} : {}));
  useEffect(() => {
    if (!assetId) return;
    let alive = true;
    void mediaUrls(assetId, version).then((u) => alive && setUrls(u));
    return () => {
      alive = false;
    };
  }, [assetId, version]);
  return urls;
}

/*
 * Bucket GETs fail now and then when many start at once (Railway buckets
 * especially: a library or timeline full of images fails a different few on
 * every load). Media loads therefore run a few at a time and retry with backoff.
 */
const RETRY_DELAYS_MS = [400, 1500, 4000];
const MAX_PARALLEL = 6;
let running = 0;
const queued: (() => void)[] = [];

async function inSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (running >= MAX_PARALLEL) await new Promise<void>((r) => queued.push(r));
  running++;
  try {
    return await fn();
  } finally {
    running--;
    queued.shift()?.();
  }
}

/** How long to wait before retry number `attempt` (0-based), or null when it is time to give up. */
export function mediaRetryDelay(attempt: number): number | null {
  return RETRY_DELAYS_MS[attempt] ?? null;
}

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await inSlot(fn);
    } catch (e) {
      const wait = mediaRetryDelay(attempt);
      if (wait === null) throw e;
      await new Promise((r) => setTimeout(r, wait));
    }
  }
}

/** Loads (and decodes) an image from a media URL, retrying failed loads. */
export function loadMediaImage(url: string): Promise<HTMLImageElement> {
  return withRetry(
    () =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image failed to load'));
        img.src = url;
      }),
  );
}

/** Fetches a media URL's bytes, retrying failed requests. */
export function fetchMediaBytes(url: string): Promise<ArrayBuffer> {
  return withRetry(async () => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.arrayBuffer();
  });
}
